import React, { useState, useRef, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { X, Send, Check, Sparkles, Upload, Loader2 } from "lucide-react";
import {
  LOGO_URL, BRAND_ORANGE, PRACTICE, PRACTICE_EMAIL, PRACTICE_ADDRESS, PRACTICE_PHONE,
  EVAL_OPTIONS, TEETH_UPPER, TEETH_LOWER, PRIMARY_UPPER, PRIMARY_LOWER,
} from "../lib/brand";
import { useAuth } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function ReferralPadModal({ open, onClose }) {
  const { user } = useAuth();
  const [form, setForm] = useState({
    patient_name: "", patient_dob: "", patient_phone: "", patient_email: "",
    referring_doctor: "", practice: "", referrer_email: "", referrer_phone: "",
    referred_to_doctor: "",
    other_reason: "", comments: "",
  });
  const [evalFor, setEvalFor] = useState([]);
  const [teeth, setTeeth] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const [extractedAt, setExtractedAt] = useState(null);
  const fileInputRef = useRef(null);
  // X-ray attachments — converted to base64 client-side and sent inside
  // the JSON payload. We never persist them in our DB; they ride the
  // referral email straight to the practice and stop there.
  const [xrays, setXrays] = useState([]); // [{ filename, content_type, data_b64, size }]
  const [xrayDragOver, setXrayDragOver] = useState(false);
  const xrayInputRef = useRef(null);
  const XRAY_MAX_FILES = 5;
  const XRAY_MAX_PER_FILE = 10 * 1024 * 1024;
  const XRAY_TYPES = ["image/jpeg", "image/jpg", "image/png", "image/heic", "application/pdf"];

  const addXrays = (fileList) => {
    const files = Array.from(fileList || []);
    if (!files.length) return;
    if (xrays.length + files.length > XRAY_MAX_FILES) {
      toast.error(`You can attach up to ${XRAY_MAX_FILES} x-rays`);
      return;
    }
    files.forEach((file) => {
      if (file.size > XRAY_MAX_PER_FILE) {
        toast.error(`"${file.name}" is over 10 MB`);
        return;
      }
      const ctype = (file.type || "").toLowerCase();
      if (ctype && !XRAY_TYPES.includes(ctype)) {
        toast.error(`"${file.name}" is not a JPG, PNG, or PDF`);
        return;
      }
      const reader = new FileReader();
      reader.onload = () => {
        // strip the data: prefix → keep just the base64 payload
        const result = reader.result;
        const b64 = typeof result === "string" ? result.split(",")[1] || "" : "";
        setXrays((cur) => {
          if (cur.length >= XRAY_MAX_FILES) return cur;
          return [...cur, { filename: file.name, content_type: ctype || "application/octet-stream", data_b64: b64, size: file.size }];
        });
      };
      reader.readAsDataURL(file);
    });
  };
  const removeXray = (idx) => setXrays((cur) => cur.filter((_, i) => i !== idx));
  const onXrayDrop = (e) => {
    e.preventDefault();
    setXrayDragOver(false);
    addXrays(e.dataTransfer?.files);
  };

  // Allow paste-from-clipboard (Cmd/Ctrl+V) while the modal is open
  useEffect(() => {
    if (!open || done) return undefined;
    const onPaste = (e) => {
      const items = e.clipboardData?.items;
      if (!items) return;
      for (const it of items) {
        if (it.kind === "file" && it.type.startsWith("image/")) {
          const blob = it.getAsFile();
          if (blob) {
            e.preventDefault();
            handleImage(blob);
            return;
          }
        }
      }
    };
    window.addEventListener("paste", onPaste);
    return () => window.removeEventListener("paste", onPaste);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, done]);

  // Prefill the referrer-side fields when a logged-in user opens the modal.
  // Backend pulls from the user's most recent referral (or profile fallback)
  // so the same doctor doesn't retype their own name/practice/contact every
  // time. Patient fields stay blank — they vary per referral.
  useEffect(() => {
    if (!open || !user) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const { data } = await axios.get(`${API}/me/refer-patient-prefill`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (cancelled) return;
        setForm((f) => ({
          ...f,
          // Only fill where the user hasn't already started typing — never
          // clobber a field with an empty server value.
          referring_doctor: f.referring_doctor || data.referring_doctor || "",
          practice: f.practice || data.practice || "",
          referrer_email: f.referrer_email || data.referrer_email || "",
          referrer_phone: f.referrer_phone || data.referrer_phone || "",
        }));
      } catch {
        // Fallback to whatever the auth context already has on `user`.
        setForm((f) => ({
          ...f,
          referring_doctor: f.referring_doctor || user.name || "",
          referrer_email: f.referrer_email || user.email || "",
        }));
      }
    })();
    return () => { cancelled = true; };
  }, [open, user]);

  if (!open) return null;

  const toggle = (arr, setter, v) => setter(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v]);
  const update = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleImage = async (file) => {
    if (!file) return;
    if (!/^image\/(png|jpe?g|webp)$/i.test(file.type)) {
      toast.error("Please use a PNG, JPG, or WEBP screenshot");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Image must be under 10 MB");
      return;
    }
    setExtracting(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name || "screenshot.png");
      const { data } = await axios.post(`${API}/public/refer-patient/extract`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      // Merge extracted fields into form (always overwrite — user can edit after)
      setForm((f) => ({
        ...f,
        patient_name: data.patient_name || f.patient_name,
        patient_dob: data.patient_dob || f.patient_dob,
        patient_phone: data.patient_phone || f.patient_phone,
        referring_doctor: data.referring_doctor || f.referring_doctor,
        practice: data.practice || f.practice,
        referrer_email: data.referrer_email || f.referrer_email,
        referrer_phone: data.referrer_phone || f.referrer_phone,
        other_reason: data.other_reason || f.other_reason,
        comments: data.comments || f.comments,
      }));
      if (Array.isArray(data.evaluate_for) && data.evaluate_for.length) {
        setEvalFor((prev) => Array.from(new Set([...prev, ...data.evaluate_for])));
      }
      if (Array.isArray(data.teeth) && data.teeth.length) {
        setTeeth((prev) => Array.from(new Set([...prev, ...data.teeth])));
      }
      setExtractedAt(new Date());
      toast.success("Fields autofilled — please review before submitting");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not extract from image");
    } finally {
      setExtracting(false);
    }
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    if (file) handleImage(file);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      // Send Authorization header (if logged in) so the backend can stamp
      // user_id on the referral — that's what /me/refer-patient-prefill
      // looks up to autofill next time.
      const token = localStorage.getItem("token");
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.post(
        `${API}/public/refer-patient`,
        {
          ...form,
          evaluate_for: evalFor,
          teeth,
          xray_files: xrays.map((x) => ({
            filename: x.filename,
            content_type: x.content_type,
            data_b64: x.data_b64,
          })),
        },
        { headers },
      );
      setDone(true);
      toast.success("Referral submitted — emailed to the practice");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Submission failed");
    } finally {
      setSubmitting(false);
    }
  };

  const close = () => { setDone(false); setXrays([]); onClose(); };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center p-4 overflow-y-auto bg-black/50" onClick={close}>
      <div className="my-8 w-full max-w-3xl bg-white rounded-xl shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()}>
        {/* Header — orange band, matches the printed pad */}
        <div className="px-6 py-5 flex items-center gap-4" style={{ backgroundColor: BRAND_ORANGE }}>
          <img src={LOGO_URL} alt="GCOMS" className="w-14 h-14 rounded-full bg-black p-1" />
          <div className="flex-1 text-white">
            <div className="text-[10px] tracking-widest uppercase opacity-90">Patient Referral</div>
            <div className="font-bold text-lg leading-tight">{PRACTICE.toUpperCase()}</div>
            <div className="text-xs opacity-95">{PRACTICE_ADDRESS} · Tel {PRACTICE_PHONE}</div>
          </div>
          <button data-testid="referral-close" onClick={close} className="text-white/90 hover:text-white p-1"><X size={20} /></button>
        </div>

        {done ? (
          <div className="p-12 text-center">
            <div className="w-16 h-16 mx-auto rounded-full flex items-center justify-center text-white" style={{ backgroundColor: BRAND_ORANGE }}>
              <Check size={32} />
            </div>
            <h3 className="font-heading text-2xl font-semibold mt-5" data-testid="referral-success">Referral sent</h3>
            <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
              The referral was emailed to {PRACTICE_EMAIL}. A printable PDF copy was sent to your
              email for the patient's chart{form.patient_email ? ", and the patient received a confirmation with our office contact info" : ""}.
            </p>
            <button onClick={close} className="mt-6 px-6 py-2.5 rounded-md text-white font-medium" style={{ backgroundColor: BRAND_ORANGE }}>Done</button>
          </div>
        ) : (
        <form onSubmit={submit} className="p-6 space-y-5 max-h-[80vh] overflow-y-auto">
          {/* Autofill from screenshot — vision extraction */}
          <div
            onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
            onDragLeave={() => setDragOver(false)}
            onDrop={onDrop}
            data-testid="referral-autofill-zone"
            className={`relative rounded-lg border-2 border-dashed p-4 transition-colors ${
              dragOver ? "border-[#F26A24] bg-[#FDE6D8]" : extracting ? "border-[#F26A24]/50 bg-[#FFF7F0]" : "border-gray-300 bg-gray-50 hover:border-[#F26A24]/60 hover:bg-[#FFF7F0]"
            }`}
          >
            <input
              ref={fileInputRef}
              data-testid="referral-autofill-file"
              type="file"
              accept="image/png,image/jpeg,image/webp"
              className="hidden"
              onChange={(e) => { const f = e.target.files?.[0]; if (f) handleImage(f); e.target.value = ""; }}
            />
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0" style={{ backgroundColor: BRAND_ORANGE, color: "white" }}>
                {extracting ? <Loader2 size={20} className="animate-spin" /> : <Sparkles size={20} />}
              </div>
              <div className="flex-1 min-w-0">
                <div className="font-semibold text-sm">Autofill from a screenshot</div>
                <div className="text-xs text-gray-600 mt-0.5">
                  Drop a screenshot of the patient's chart here, paste it (⌘V / Ctrl+V), or{" "}
                  <button
                    type="button"
                    data-testid="referral-autofill-browse"
                    onClick={() => fileInputRef.current?.click()}
                    className="underline font-medium hover:text-[#F26A24]"
                    disabled={extracting}
                  >
                    browse for an image
                  </button>
                  . PNG, JPG, or WEBP up to 10 MB.
                </div>
                {extracting && (
                  <div className="text-xs text-[#D85819] font-medium mt-2 flex items-center gap-1.5" data-testid="referral-extract-status">
                    <Loader2 size={12} className="animate-spin" /> Reading the screenshot — usually takes 5–10 seconds…
                  </div>
                )}
                {!extracting && extractedAt && (
                  <div className="text-xs text-sage-deep font-medium mt-2 flex items-center gap-1.5" data-testid="referral-extract-done">
                    <Check size={12} /> Fields autofilled — please review for accuracy before submitting.
                  </div>
                )}
              </div>
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                disabled={extracting}
                data-testid="referral-autofill-button"
                className="shrink-0 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium border border-gray-300 bg-white hover:border-[#F26A24] disabled:opacity-60"
              >
                <Upload size={12} /> Upload
              </button>
            </div>
            <div className="text-[11px] text-gray-500 mt-2 leading-relaxed">
              The image is analyzed by AI to extract fields then discarded — it is not stored. Strip PHI you don't want transmitted before uploading.
            </div>
          </div>

          {/* Patient + Referring doctor */}
          <div className="grid sm:grid-cols-2 gap-3">
            <FieldInput label="Patient name *" testid="ref-patient-name" required value={form.patient_name} onChange={(v) => update("patient_name", v)} />
            <FieldInput label="Date of birth" testid="ref-patient-dob" type="date" value={form.patient_dob} onChange={(v) => update("patient_dob", v)} />
            <FieldInput label="Patient phone *" testid="ref-patient-phone" required value={form.patient_phone} onChange={(v) => update("patient_phone", v)} />
            <FieldInput label="Patient email (optional)" testid="ref-patient-email" type="email" value={form.patient_email} onChange={(v) => update("patient_email", v)} hint="Patient receives a confirmation with our office info" />
            <FieldInput label="Referring doctor *" testid="ref-doctor" required value={form.referring_doctor} onChange={(v) => update("referring_doctor", v)} />
            <FieldInput label="Practice" testid="ref-practice" value={form.practice} onChange={(v) => update("practice", v)} />
            <FieldInput label="Your email *" testid="ref-email" type="email" required value={form.referrer_email} onChange={(v) => update("referrer_email", v)} />
            <FieldInput label="Your phone" testid="ref-phone" value={form.referrer_phone} onChange={(v) => update("referrer_phone", v)} />
          </div>

          {/* Referred-to surgeon (optional) */}
          <div>
            <div className="text-sm font-semibold mb-2">Refer to <span className="text-gray-500 font-normal">(optional — leave blank for either available surgeon)</span></div>
            <div className="grid sm:grid-cols-2 gap-2" data-testid="ref-refer-to-group">
              {["Gregory Yunov DDS, MD", "Steve Yusupov DDS, MD"].map((name) => {
                const selected = form.referred_to_doctor === name;
                return (
                  <label
                    key={name}
                    className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-sm ${
                      selected ? "border-[#F26A24] bg-[#FDE6D8]" : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <input
                      type="radio"
                      name="referred_to_doctor"
                      data-testid={`ref-refer-to-${name.split(" ")[0].toLowerCase()}`}
                      checked={selected}
                      // Click again to clear selection (optional field).
                      onClick={() => { if (selected) update("referred_to_doctor", ""); }}
                      onChange={() => update("referred_to_doctor", name)}
                      className="accent-[#F26A24]"
                    />
                    {name}
                  </label>
                );
              })}
            </div>
          </div>

          {/* Evaluate for */}
          <div>
            <div className="text-sm font-semibold mb-2">Please evaluate for</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {EVAL_OPTIONS.map((opt) => (
                <label key={opt} className={`flex items-center gap-2 p-2.5 rounded-md border cursor-pointer text-sm ${evalFor.includes(opt) ? "border-[#F26A24] bg-[#FDE6D8]" : "border-gray-200 hover:bg-gray-50"}`}>
                  <input type="checkbox" data-testid={`ref-eval-${opt.toLowerCase().replace(/[^a-z]/g,'-')}`} checked={evalFor.includes(opt)} onChange={() => toggle(evalFor, setEvalFor, opt)} className="accent-[#F26A24]" />
                  {opt}
                </label>
              ))}
            </div>
            <input
              data-testid="ref-other"
              placeholder="Other reason / details"
              value={form.other_reason}
              onChange={(e) => update("other_reason", e.target.value)}
              className="mt-2 w-full px-3 py-2 rounded-md border border-gray-200 text-sm"
            />
          </div>

          {/* Tooth chart */}
          <div>
            <div className="text-sm font-semibold mb-2">Tooth chart <span className="text-gray-500 font-normal">(click to mark)</span></div>
            <ToothChart selected={teeth} onToggle={(t) => toggle(teeth, setTeeth, t)} />
          </div>

          {/* Comments */}
          <div>
            <label className="text-sm font-semibold">Comments</label>
            <textarea
              data-testid="ref-comments"
              value={form.comments}
              onChange={(e) => update("comments", e.target.value)}
              rows={3}
              className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm"
              placeholder="Any additional clinical details, images attached separately, urgency, etc."
            />
          </div>

          {/* X-ray attachments — emailed straight to the practice */}
          <div>
            <div className="text-sm font-semibold mb-2">Attach x-rays <span className="text-gray-500 font-normal">(optional)</span></div>
            <div
              onDragOver={(e) => { e.preventDefault(); setXrayDragOver(true); }}
              onDragLeave={() => setXrayDragOver(false)}
              onDrop={onXrayDrop}
              data-testid="referral-xray-zone"
              className={`rounded-lg border-2 border-dashed p-4 transition-colors ${
                xrayDragOver ? "border-[#F26A24] bg-[#FDE6D8]" : "border-gray-300 bg-gray-50 hover:border-[#F26A24]/60 hover:bg-[#FFF7F0]"
              }`}
            >
              <input
                ref={xrayInputRef}
                data-testid="referral-xray-input"
                type="file"
                accept="image/jpeg,image/jpg,image/png,image/heic,application/pdf"
                multiple
                className="hidden"
                onChange={(e) => { addXrays(e.target.files); e.target.value = ""; }}
              />
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-md flex items-center justify-center shrink-0 bg-white border border-gray-200 text-gray-500">
                  <Upload size={18} />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm">Drop x-rays here, or{" "}
                    <button
                      type="button"
                      data-testid="referral-xray-browse"
                      onClick={() => xrayInputRef.current?.click()}
                      className="underline font-medium hover:text-[#F26A24]"
                    >
                      browse files
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 mt-1">
                    JPG, PNG, or PDF. Up to {XRAY_MAX_FILES} files, 10 MB each. Attached to the email sent to the practice — not stored on the server.
                  </div>
                </div>
              </div>
              {xrays.length > 0 && (
                <ul className="mt-3 space-y-1.5" data-testid="referral-xray-list">
                  {xrays.map((x, i) => (
                    <li key={`${x.filename}-${i}`} className="flex items-center justify-between text-xs bg-white border border-gray-200 rounded-md px-3 py-2">
                      <span className="truncate pr-3">
                        <span className="font-medium">{x.filename}</span>
                        <span className="text-gray-500 ml-2">{(x.size / (1024 * 1024)).toFixed(2)} MB</span>
                      </span>
                      <button
                        type="button"
                        data-testid={`referral-xray-remove-${i}`}
                        onClick={() => removeXray(i)}
                        className="text-gray-400 hover:text-red-600"
                      >
                        <X size={14} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={close} className="px-5 py-2.5 rounded-md border border-gray-200 text-sm">Cancel</button>
            <button data-testid="ref-submit" type="submit" disabled={submitting} className="inline-flex items-center gap-2 px-6 py-2.5 rounded-md text-white font-medium disabled:opacity-60" style={{ backgroundColor: BRAND_ORANGE }}>
              <Send size={16} /> {submitting ? "Sending..." : "Submit referral"}
            </button>
          </div>
        </form>
        )}
      </div>
    </div>
  );
}

function FieldInput({ label, testid, type = "text", required, value, onChange, hint }) {
  return (
    <label className="block">
      <span className="text-xs uppercase tracking-wider font-medium text-gray-600">{label}</span>
      <input
        data-testid={testid}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="mt-1 w-full px-3 py-2 rounded-md border border-gray-200 text-sm focus:outline-none focus:border-[#F26A24] focus:ring-1 focus:ring-[#F26A24]"
      />
      {hint && <span className="block text-[10px] text-gray-400 mt-0.5 leading-tight">{hint}</span>}
    </label>
  );
}

function ToothChart({ selected, onToggle }) {
  const isSel = (t) => selected.includes(t);
  const Cell = ({ t, label }) => (
    <button
      type="button"
      data-testid={`tooth-${t}`}
      onClick={() => onToggle(t)}
      className={`w-7 h-7 text-[10px] font-mono rounded border ${isSel(t) ? "text-white border-transparent" : "border-gray-200 text-gray-700 hover:border-gray-400"}`}
      style={isSel(t) ? { backgroundColor: BRAND_ORANGE } : undefined}
    >{label}</button>
  );
  return (
    <div className="border border-gray-200 rounded-md p-3 text-center font-mono">
      <div className="flex items-center justify-center gap-1.5 mb-1">
        {PRIMARY_UPPER.map((l) => <Cell key={`pu-${l}`} t={l} label={l} />)}
      </div>
      <div className="flex items-center justify-center gap-1.5">
        <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ backgroundColor: BRAND_ORANGE }}>R</span>
        {TEETH_UPPER.map((n) => <Cell key={`tu-${n}`} t={`#${n}`} label={n} />)}
        <span className="w-6 h-6 rounded-full text-white text-xs font-bold flex items-center justify-center" style={{ backgroundColor: BRAND_ORANGE }}>L</span>
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-1">
        <span className="w-6" />
        {TEETH_LOWER.map((n) => <Cell key={`tl-${n}`} t={`#${n}`} label={n} />)}
        <span className="w-6" />
      </div>
      <div className="flex items-center justify-center gap-1.5 mt-1">
        {PRIMARY_LOWER.map((l) => <Cell key={`pl-${l}`} t={l} label={l} />)}
      </div>
    </div>
  );
}
